import { Icon } from '@chakra-ui/react'
import { lazy, ReactNode, Suspense } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import { ShieldTick, Stars } from '../utils/constants/icons'
import ReactWrapper from '../components/common/layout/ReactWrapper'
import TabWrapper from '../components/common/layout/TabWrapper'
import { useAuth } from '../hooks/useAuth'
import { LoginRedirect } from './PublicRoutes'

// Lazy imports - Private pages
const HelpCenter = lazy(() => import('../pages/Help/HelpCenter'))
const ReactUsers = lazy(() => import('../pages/react/Users/Users'))
const ReactUserDetail = lazy(() => import('../pages/react/Users/UserDetail'))
const ProtectedImageListPage = lazy(() => import('../pages/react/PortfolioShield/ProtectedImageList'))
const ViewProtectedImage = lazy(() => import('../pages/react/PortfolioShield/ViewProtectedImage'))
const BatchDetailPage = lazy(() => import('../pages/react/PortfolioShield/BatchDetailPage'))
const ComparisonTasksTable = lazy(() => import('../pages/react/PortfolioShield/Search/SearchTaskTable'))
const ExchangeImport = lazy(() => import('../pages/react/ExchangeCenter/OceanProImport'))
const ExchangeExport = lazy(() => import('../pages/react/ExchangeCenter/OceanProExport'))
const CustomEntitiesOverview = lazy(() => import('../pages/react/CustomEntities/Overview'))
const CustomEntityList = lazy(() => import('../pages/react/CustomEntities/EntityList'))
const CustomSeizureList = lazy(() => import('../pages/react/CustomEntities/SeizureList'))
const CustomSeizureDetail = lazy(() => import('../pages/react/CustomEntities/SeizureDetail'))
const CustomProductList = lazy(() => import('../pages/react/CustomEntities/ProductList'))
const ReactNotifications = lazy(() => import('../pages/react/Notifications/Notifications'))
const Reporting = lazy(() => import('../pages/react/Reporting/Reporting'))
const ReportCreate = lazy(() => import('../pages/react/Reporting/ReportCreate'))

const ReactOnboarding = lazy(() => import('../pages/react/Onboarding/Onboarding'))
const ReactOnboardingExpand = lazy(() => import('../pages/react/Onboarding/OnboardingExpand'))
const ReactOnboardingTemplateExpand = lazy(() => import('../pages/react/Onboarding/OnboardingTemplateExpand'))

const ReactPlatforms = lazy(() => import('../pages/react/Platforms/Platforms'))
const ReactPlatformsList = lazy(() => import('../pages/react/Platforms/PlatformsList'))
const ReactPlatformsExpand = lazy(() => import('../pages/react/Platforms/PlatformsExpand'))
const ReactPlatformsAddEdit = lazy(() => import('../pages/react/Platforms/PlatformsAddEdit'))

const ReactAiClassifierICR = lazy(() => import('../pages/react/Ai/AiClassifierICR'))
const ReactAiClassifierTM = lazy(() => import('../pages/react/Ai/AiClassifierTM'))
const ReactAiClassifierLogo = lazy(() => import('../pages/react/Ai/AiClassifierLogo'))
const ReactAiClassifierBrandMisuse = lazy(() => import('../pages/react/Ai/AiClassifierBrandMisuse.tsx'))

const ReactAiClassifierTrainingJobs = lazy(() => import('../pages/react/Ai/AiClassifierTrainingJobs'))

const ReactStandalone = lazy(() => import('../pages/react/Saw/Standalone'))
const ReactStandaloneList = lazy(() => import('../pages/react/Saw/StandaloneList'))
const ReactStandaloneItemsList = lazy(() => import('../pages/react/Saw/StandaloneItemsList'))
const ReactStandaloneIntermediaries = lazy(() => import('../pages/react/Saw/StandaloneIntermediaries'))
const ReactDataProviderSearch = lazy(() => import('../pages/react/Saw/DataProviderSearch'))
const DomainExpand = lazy(() => import('../pages/react/Saw/DomainExpand'))

const ReactTickets = lazy(() => import('../pages/react/Tickets/Tickets'))

const ReactDashboardData = lazy(() => import('../pages/react/Dashboard/DashboardData'))

const WhitelistDomain = lazy(() => import('../pages/react/Whitelist/WhitelistDomain'))
const WhitelistSeller = lazy(() => import('../pages/react/Whitelist/WhitelistSeller'))

const ReactSellers = lazy(() => import('../pages/react/Sellers/Sellers'))
const ReactSellersExpand = lazy(() => import('../pages/react/Sellers/SellersExpand'))
const ReactSellersAddEdit = lazy(() => import('../pages/react/Sellers/SellersAddEdit'))

const ReactMembers = lazy(() => import('../pages/react/Members/Members'))
const ReactMembersList = lazy(() => import('../pages/react/Members/MembersList'))
const ReactMemberExpand = lazy(() => import('../pages/react/Members/MemberExpand'))
const ReactMemberAddEdit = lazy(() => import('../pages/react/Members/MemberAddEdit'))
const ReactBrandAddEdit = lazy(() => import('../pages/react/Members/BrandAddEdit'))
const ReactAiTokens = lazy(() => import('../pages/react/Members/AiTokens'))

const ReactHourRegistrationsOverview = lazy(() => import('../pages/react/HourRegistrations/HourRegistrationsOverview'))
const ReactHourRegistrationsRetainers = lazy(() => import('../pages/react/HourRegistrations/HourRegistrationsRetainers'))
const ReactHourRegistrationsHours = lazy(() => import('../pages/react/HourRegistrations/HourRegistrationsHours'))

const MemberReview = lazy(() => import('../pages/member/Review'))
const MemberOrganization = lazy(() => import('../pages/member/Organization'))

const ReactSystemSettings = lazy(() => import('../pages/react/System/Settings'))
const ReactSystemDataIngestion = lazy(() => import('../pages/react/System/DataIngestion'))
const ReactSystemHealthAndMaintenance = lazy(() => import('../pages/react/System/HealthAndMaintenance'))
const ReactSystemCurrencies = lazy(() => import('../pages/react/System/Currencies'))
const ReactSystemNotices = lazy(() => import('../pages/react/System/Notices'))

const ReactIntelligenceList = lazy(() => import('../pages/react/Intelligence/IntelligenceList'))
const ReactIntelligenceExpand = lazy(() => import('../pages/react/Intelligence/IntelligenceExpand'))
const ReactIntelligenceSearch = lazy(() => import('../pages/react/Intelligence/IntelligenceSearch'))
const ReactIntelligenceDeepSearch = lazy(() => import('../pages/react/Intelligence/IntelligenceDeepSearch'))

const ReactItems = lazy(() => import('../pages/react/Items/Items'))
const ReactItemList = lazy(() => import('../pages/react/Items/ItemList'))
const ReactItemDetail = lazy(() => import('../pages/react/Items/ItemExpand'))
const ReactItemImports = lazy(() => import('../pages/react/Items/ItemImports'))
const ReactItemExports = lazy(() => import('../pages/react/Items/ItemExports'))
const ReactItemAddManual = lazy(() => import('../pages/react/Items/AddManual'))

const ReactPipeline = lazy(() => import('../pages/react/Pipeline/Pipeline'))
const ReactPipelineAiTasks = lazy(() => import('../pages/react/Pipeline/AiTasks'))
const ReactPipelineTasks = lazy(() => import('../pages/react/Pipeline/Tasks'))
const ReactPipelineTasksAddEdit = lazy(() => import('../pages/react/Pipeline/TaskAddEdit'))
const ReactPipelineCrawling = lazy(() => import('../pages/react/Pipeline/Crawling'))
const ReactPipelineDetection = lazy(() => import('../pages/react/Pipeline/Detection'))
const ReactPipelineReviewing = lazy(() => import('../pages/react/Pipeline/Reviewing'))
const ReactPipelineNOA = lazy(() => import('../pages/react/Pipeline/NOA'))

const KrakenAI = lazy(() => import('../pages/react/Kraken/KrakenAI'))
const KrakenO2O = lazy(() => import('../pages/react/Kraken/KrakenO2O'))

const O2OOverview = lazy(() => import('../pages/react/Kraken/O2OOverview'))

export const PrivateRouteWrapper = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth()
  if (!isAuthenticated) return <LoginRedirect />
  return (
    <ReactWrapper>
      <Suspense fallback={<div />}>{children}</Suspense>
    </ReactWrapper>
  )
}

export const PrivateHelpCenterRouteWrapper = () => {
  return <HelpCenter />
}

export const PrivateRoutes = ({
  reviewStatus,
  permissions,
  location,
}: {
  reviewStatus: any
  permissions: {
    isSuperAdmin: boolean
    isAdmin: boolean
    isReact: boolean
    isMember: boolean
    isMemberPro: boolean
    isReactOnlineEnf: boolean
    isReactPartner: boolean
  }
  location: ReturnType<typeof useLocation>
}) => {
  return (
    <>
      <Route
        path="react/*"
        element={
          <PrivateRouteWrapper>
            <Routes>
              {permissions.isAdmin && (
                <Route
                  path="users/*"
                  element={
                    <Routes>
                      <Route path="/" element={<ReactUsers />} />
                      <Route path="/:id" element={<ReactUserDetail />} />
                    </Routes>
                  }
                />
              )}
              {(permissions.isSuperAdmin || permissions.isMemberPro) && (
                <Route
                  path="portfolio-shield/*"
                  element={
                    <TabWrapper
                      tabRoutes={[
                        {
                          label: (
                            <>
                              <Icon color="primary.300" mr="6px">
                                <ShieldTick />
                              </Icon>
                              Protected Images
                            </>
                          ),
                          path: '/react/portfolio-shield/',
                        },
                        {
                          label: 'Search',
                          path: '/react/portfolio-shield/search',
                        },
                      ]}
                    >
                      <Routes>
                        <Route path="/" element={<ProtectedImageListPage />} />
                        <Route path="/view/:id" element={<ViewProtectedImage />} />
                        <Route path="/batch/:batchId" element={<BatchDetailPage />} />
                        <Route path="/search" element={<ComparisonTasksTable />} />
                      </Routes>
                    </TabWrapper>
                  }
                />
              )}
              {(permissions.isSuperAdmin || permissions.isMemberPro) && (
                <Route
                  path="custom-entities/*"
                  element={
                    <TabWrapper
                      tabRoutes={[
                        { label: 'Overview', path: '/react/custom-entities' },
                        { label: 'Entities', path: '/react/custom-entities/entities' },
                        { label: 'Seizures', path: '/react/custom-entities/seizures', matchSubroutes: true },
                        { label: 'Products', path: '/react/custom-entities/products' },
                      ]}
                    >
                      <Routes>
                        <Route path="/" element={<CustomEntitiesOverview />} />
                        <Route path="/entities" element={<CustomEntityList />} />
                        <Route path="/seizures" element={<CustomSeizureList />} />
                        <Route path="/seizures/:id" element={<CustomSeizureDetail />} />
                        <Route path="/products" element={<CustomProductList />} />
                      </Routes>
                    </TabWrapper>
                  }
                />
              )}

              {(permissions.isSuperAdmin || permissions.isMemberPro) && (
                <Route
                  path="exchange-center/*"
                  element={
                    <TabWrapper
                      tabRoutes={[
                        { label: 'Imports', path: '/react/exchange-center' },
                        { label: 'Exports', path: '/react/exchange-center/exports' },
                      ]}
                    >
                      <Routes>
                        <Route path="/" element={<ExchangeImport />} />
                        <Route path="/exports" element={<ExchangeExport />} />
                      </Routes>
                    </TabWrapper>
                  }
                />
              )}

              <Route
                path="reporting/*"
                element={
                  <Routes>
                    <Route path="/" element={<Reporting />} />
                    {(permissions.isReact || permissions.isMemberPro) && (
                      <Route path="/create" element={<ReportCreate />} />
                    )}
                    <Route path="/:id" element={<Reporting />} />
                  </Routes>
                }
              />


              <Route
                path="notifications/*"
                element={
                  <Routes>
                    <Route path="/" element={<ReactNotifications />} />
                  </Routes>
                }
              />

              <Route
                path="onboarding/*"
                element={
                  <TabWrapper
                    tabRoutes={
                      permissions.isAdmin
                        ? [
                            {
                              label: 'Onboardings',
                              path: '/react/onboarding/list/0',
                            },
                            {
                              label: 'Templates',
                              path: '/react/onboarding/list/1',
                            },
                          ]
                        : [
                            {
                              label: 'Onboardings',
                              path: '/react/onboarding/list/0',
                            },
                          ]
                    }
                  >
                    <Routes>
                      <Route path="/list/:type" element={<ReactOnboarding />} />
                      <Route path="/:id" element={<ReactOnboardingExpand />} />
                      <Route path="/template/:id" element={<ReactOnboardingTemplateExpand />} />
                    </Routes>
                  </TabWrapper>
                }
              />

              <Route
                path="platforms/*"
                element={
                  <TabWrapper
                    tabRoutes={[
                      { label: 'Overview', path: '/react/platforms' },
                      { label: 'Data', path: '/react/platforms/list' },
                    ]}
                  >
                    <Routes>
                      <Route path="/" element={<ReactPlatforms />} />
                      <Route path="/list" element={<ReactPlatformsList />} />
                      <Route path="/add" element={<ReactPlatformsAddEdit />} />
                      <Route path="/edit/:id" element={<ReactPlatformsAddEdit />} />
                      <Route path="/:id" element={<ReactPlatformsExpand />} />
                    </Routes>
                  </TabWrapper>
                }
              />

              {permissions.isAdmin && (
                <Route
                  path="ai/*"
                  element={
                    <TabWrapper
                      tabRoutes={[
                        { label: 'Logo', path: '/react/ai/logo' },
                        { label: 'Trademark', path: '/react/ai/tm' },
                        { label: 'Image Copyright', path: '/react/ai/icr' },
                        { label: 'Brand Misuse', path: '/react/ai/brand-misuse' },
                        { label: 'Training Jobs', path: '/react/ai/training-jobs' },
                      ]}
                    >
                      <Routes>
                        <Route path="/logo" element={<ReactAiClassifierLogo />} />
                        <Route path="/tm" element={<ReactAiClassifierTM />} />
                        <Route path="/icr" element={<ReactAiClassifierICR />} />
                        <Route path="/brand-misuse" element={<ReactAiClassifierBrandMisuse />} />
                        <Route path="/training-jobs" element={<ReactAiClassifierTrainingJobs />} />
                      </Routes>
                    </TabWrapper>
                  }
                />
              )}

              <Route
                path="saw/*"
                element={
                  <TabWrapper
                    tabRoutes={[
                      { label: 'Overview', path: '/react/saw' },
                      { label: 'Domains', path: '/react/saw/sites' },
                      {
                        label: 'Site Items',
                        path: '/react/saw/listings',
                      },
                      ...(permissions.isReact
                        ? [
                            {
                              label: 'Intermediaries',
                              path: '/react/saw/intermediaries',
                            },
                          ]
                        : []),
                      ...(permissions.isSuperAdmin
                        ? [
                            {
                              label: 'Search Engine',
                              path: '/react/saw/search-engine',
                            },
                          ]
                        : []),
                    ]}
                  >
                    <Routes>
                      <Route path="/" element={<ReactStandalone />} />
                      <Route path="/sites" element={<ReactStandaloneList />} />
                      <Route path="/sites/:id" element={<DomainExpand />} />
                      <Route path="/listings" element={<ReactStandaloneItemsList />} />
                      {permissions.isReact && (
                        <Route path="/intermediaries" element={<ReactStandaloneIntermediaries />} />
                      )}
                      {permissions.isSuperAdmin && (
                        <Route path="/search-engine" element={<ReactDataProviderSearch />} />
                      )}
                    </Routes>
                  </TabWrapper>
                }
              />

              <Route path="tickets/*" element={<ReactTickets />} />

              <Route
                path="dashboard/*"
                element={
                  <Routes>
                    <Route path="/" element={<ReactDashboardData />} />
                  </Routes>
                }
              />

              <Route
                path="whitelist/*"
                element={
                  <TabWrapper
                    tabRoutes={[
                      { label: 'Sellers', path: '/react/whitelist/sellers' },
                      { label: 'Domains', path: '/react/whitelist/domains' },
                    ]}
                  >
                    <Routes>
                      <Route path="/sellers" element={<WhitelistSeller />} />
                      <Route path="/domains" element={<WhitelistDomain />} />
                    </Routes>
                  </TabWrapper>
                }
              />

              <Route
                path="sellers/*"
                element={
                  <TabWrapper
                    tabRoutes={[
                      {
                        label: 'Platform Sellers',
                        path: '/react/sellers/list/platform',
                      },
                      {
                        label: 'Standalone Sellers',
                        path: '/react/sellers/list/standalone',
                      },
                    ]}
                  >
                    <Routes>
                      <Route path="/list/:filter" element={<ReactSellers />} />
                      <Route path="/add" element={<ReactSellersAddEdit />} />
                      <Route path="/edit/:id" element={<ReactSellersAddEdit />} />
                      <Route path="/:id" element={<ReactSellersExpand />} />
                    </Routes>
                  </TabWrapper>
                }
              />

              <Route
                path="members/*"
                element={
                  <Routes>
                    <Route path="/" element={<ReactMembers />} />
                    <Route path="/list" element={<ReactMembersList />} />
                    <Route path="/add" element={<ReactMemberAddEdit />} />
                    <Route path="/edit/:id" element={<ReactMemberAddEdit />} />
                    <Route path="/:id" element={<ReactMemberExpand />} />

                    <Route path="/:member_id/brand/add" element={<ReactBrandAddEdit />} />
                    <Route path="/:member_id/brand/edit/:id" element={<ReactBrandAddEdit />} />
                    {permissions.isSuperAdmin && (
                      <Route path="/:member_id/brand/:brand_id/ai-tokens" element={<ReactAiTokens />} />
                    )}
                  </Routes>
                }
              />

              <Route
                path="hour-registrations/*"
                element={
                  <TabWrapper
                    tabRoutes={[
                      { label: 'Overview', path: '/react/hour-registrations' },
                      { label: 'Retainers', path: '/react/hour-registrations/retainers' },
                      { label: 'Hours', path: '/react/hour-registrations/hours' },
                    ]}
                  >
                    <Routes>
                      <Route path="/" element={<ReactHourRegistrationsOverview />} />
                      <Route path="/retainers" element={<ReactHourRegistrationsRetainers />} />
                      <Route path="/hours" element={<ReactHourRegistrationsHours />} />
                    </Routes>
                  </TabWrapper>
                }
              />

              <Route
                path="items/*"
                element={
                  <TabWrapper
                    tabRoutes={
                      permissions.isMember && !permissions.isReact
                        ? [
                            { label: 'Overview', path: '/react/items/overview' },
                            { label: 'All', path: '/react/items/list/0' },
                          ]
                        : [
                            { label: 'Overview', path: '/react/items/overview' },
                            { label: 'All', path: '/react/items/list/0' },
                            { label: 'Crawled Items', path: '/react/items/list/1' },
                            { label: 'Manual Items', path: '/react/items/list/2' },
                            { label: 'Legacy Items', path: '/react/items/list/3' },
                            { label: 'Imports', path: '/react/items/imports' },
                            { label: 'Exports', path: '/react/items/exports' },
                          ]
                    }
                  >
                    <Routes>
                      <Route path="/overview" element={<ReactItems />} />
                      <Route path="/:id" element={<ReactItemDetail />} />
                      <Route path="/list/:f" element={<ReactItemList />} />
                      <Route path="/imports" element={<ReactItemImports />} />
                      <Route path="/exports" element={<ReactItemExports />} />
                      <Route path="/add-manual" element={<ReactItemAddManual />} />
                    </Routes>
                  </TabWrapper>
                }
              />

              <Route
                path="intelligence/*"
                element={
                  <TabWrapper
                    tabRoutes={
                      location.pathname === '/react/intelligence/search'
                        ? permissions.isAdmin
                          ? [
                              { label: 'Monitoring', path: '/react/pipeline' },
                              { label: 'Reviewing', path: '/react/pipeline/4' },
                              {
                                label: (
                                  <>
                                    <Icon color="primary.300" mr="6px">
                                      <Stars />
                                    </Icon>
                                    AI Tasks
                                  </>
                                ),
                                path: '/react/pipeline/0',
                              },
                              { label: 'Tasks', path: '/react/pipeline/1' },
                              { label: 'Crawling', path: '/react/pipeline/2' },
                              { label: 'Notices', path: '/react/pipeline/5' },
                              {
                                label: (
                                  <>
                                    <Icon color="primary.300" mr="6px">
                                      <Stars />
                                    </Icon>
                                    Live Search
                                  </>
                                ),
                                path: '/react/intelligence/search',
                              },
                            ]
                          : [
                              { label: 'Monitoring', path: '/react/pipeline' },
                              { label: 'Reviewing', path: '/react/pipeline/4' },
                              { label: 'Tasks', path: '/react/pipeline/1' },
                              { label: 'Crawling', path: '/react/pipeline/2' },
                              { label: 'Notices', path: '/react/pipeline/5' },
                              {
                                label: (
                                  <>
                                    <Icon color="primary.300" mr="6px">
                                      <Stars />
                                    </Icon>
                                    Live Search
                                  </>
                                ),
                                path: '/react/intelligence/search',
                              },
                            ]
                        : [
                            {
                              label: 'All Cases',
                              path: '/react/intelligence/list/0',
                            },
                            {
                              label: 'Open Cases',
                              path: '/react/intelligence/list/1',
                            },
                            {
                              label: 'Closed Cases',
                              path: '/react/intelligence/list/2',
                            },
                          ]
                    }
                  >
                    <Routes>
                      <Route path="/:id" element={<ReactIntelligenceExpand />} />
                      <Route path="/list/:filter" element={<ReactIntelligenceList />} />
                      <Route path="/deep" element={<ReactIntelligenceDeepSearch />} />
                      <Route path="/search" element={<ReactIntelligenceSearch />} />
                    </Routes>
                  </TabWrapper>
                }
              />

              <Route
                path="pipeline/*"
                element={
                  <TabWrapper
                    tabRoutes={
                      permissions.isAdmin
                        ? [
                            { label: 'Monitoring', path: '/react/pipeline' },
                            { label: 'Reviewing', path: '/react/pipeline/4' },
                            {
                              label: (
                                <>
                                  <Icon color="primary.300" mr="6px">
                                    <Stars />
                                  </Icon>
                                  AI Tasks
                                </>
                              ),
                              path: '/react/pipeline/0',
                            },
                            { label: 'Tasks', path: '/react/pipeline/1' },
                            { label: 'Crawling', path: '/react/pipeline/2' },
                            { label: 'Notices', path: '/react/pipeline/5' },
                            {
                              label: (
                                <>
                                  <Icon color="primary.300" mr="6px">
                                    <Stars />
                                  </Icon>
                                  Live Search
                                </>
                              ),
                              path: '/react/intelligence/search',
                            },
                          ]
                        : [
                            { label: 'Monitoring', path: '/react/pipeline' },
                            { label: 'Reviewing', path: '/react/pipeline/4' },
                            { label: 'Tasks', path: '/react/pipeline/1' },
                            { label: 'Crawling', path: '/react/pipeline/2' },
                            { label: 'Notices', path: '/react/pipeline/5' },
                            {
                              label: (
                                <>
                                  <Icon color="primary.300" mr="6px">
                                    <Stars />
                                  </Icon>
                                  Live Search
                                </>
                              ),
                              path: '/react/intelligence/search',
                            },
                          ]
                    }
                  >
                    <Routes>
                      <Route path="/" element={<ReactPipeline />} />
                      <Route path="/0" element={<ReactPipelineAiTasks />} />
                      <Route path="/1" element={<ReactPipelineTasks />} />
                      <Route path="/2" element={<ReactPipelineCrawling />} />
                      <Route path="/3" element={<ReactPipelineDetection />} />
                      <Route path="/4" element={<ReactPipelineReviewing />} />
                      <Route path="/5" element={<ReactPipelineNOA />} />
                      <Route path="/add-task" element={<ReactPipelineTasksAddEdit />} />
                      <Route path="/edit-task/:id" element={<ReactPipelineTasksAddEdit />} />
                    </Routes>
                  </TabWrapper>
                }
              />

              {permissions.isSuperAdmin && (
                <Route
                  path="system/*"
                  element={
                    <TabWrapper
                      tabRoutes={[
                        { label: 'Health & Maintenance', path: '/react/system' },
                        { label: 'Data Ingestion', path: '/react/system/0' },
                        { label: 'Currencies', path: '/react/system/1' },
                        { label: 'Settings', path: '/react/system/2' },
                        { label: 'Notices', path: '/react/system/3' },
                      ]}
                    >
                      <Routes>
                        <Route path="/" element={<ReactSystemHealthAndMaintenance />} />
                        <Route path="/0" element={<ReactSystemDataIngestion />} />
                        <Route path="/1" element={<ReactSystemCurrencies />} />
                        <Route path="/2" element={<ReactSystemSettings />} />
                        <Route path="/3" element={<ReactSystemNotices />} />
                      </Routes>
                    </TabWrapper>
                  }
                />
              )}
              {(permissions.isSuperAdmin || permissions.isMemberPro) && (
                <Route
                  path="kraken/*"
                  element={
                    <TabWrapper
                      tabRoutes={[
                        { label: 'Ocean AI', path: '/react/kraken' },
                        { label: 'O2O Graphs', path: '/react/kraken/o2o' },
                      ]}
                    >
                      <Routes>
                        <Route path="/" element={<KrakenAI />} />
                        <Route path="/o2o" element={<KrakenO2O />} />
                        <Route path="/o2o/:id" element={<KrakenO2O />} />
                        <Route path="/o2o-overview" element={<O2OOverview />} />
                      </Routes>
                    </TabWrapper>
                  }
                />
              )}
            </Routes>
          </PrivateRouteWrapper>
        }
      />
      <Route
        path="member/*"
        element={
          <PrivateRouteWrapper>
            <Routes>
              <Route
                path="review/*"
                element={
                  <TabWrapper
                    tabRoutes={[
                      {
                        label: 'Platform Items',
                        path: '/member/review/platform_items',
                        hasExclamation: reviewStatus.data?.platform_items,
                      },
                      {
                        label: 'Site Items',
                        path: '/member/review/site_items',
                        hasExclamation: reviewStatus.data?.site_items,
                      },
                      {
                        label: 'Sites',
                        path: '/member/review/domains',
                        hasExclamation: reviewStatus.data?.saws,
                      },
                    ]}
                  >
                    <Routes>
                      <Route path="/:type" element={<MemberReview />} />
                    </Routes>
                  </TabWrapper>
                }
              />
              <Route path="organization/*" element={<MemberOrganization />} />
              {permissions.isSuperAdmin && (
                <Route path="brand/:brand_id/ai-tokens" element={<ReactAiTokens />} />
              )}
            </Routes>
          </PrivateRouteWrapper>
        }
      />
    </>
  )
}
